/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** @sfdc-extension-file SFDC_EXT_PRODUCT_CONTENT */
import { type ReactElement } from 'react';
import { Typography } from '@/components/typography';
import HtmlFragment from '@/components/html-fragment';
import type { SizeGuideData } from '@/extensions/product-content/lib/api/product-content.server';

/**
 * Renders size guide modal content.
 * Layout: conversion chart, how-to-measure steps, size tips — unless `htmlContent` is present,
 * in which case that raw HTML (e.g. a migrated legacy Content Asset) is rendered instead.
 */
export function SizeGuideModalContent({ sizeGuideData }: { sizeGuideData: SizeGuideData }): ReactElement {
    const { subtitle, htmlContent, chart, howToMeasure, sizeTips } = sizeGuideData;

    if (htmlContent) {
        return (
            <>
                {subtitle && (
                    <Typography as="p" className="text-sm text-muted-foreground">
                        {subtitle}
                    </Typography>
                )}
                <HtmlFragment content={htmlContent.html} contentType={htmlContent.contentType} />
            </>
        );
    }

    return (
        <>
            {subtitle && (
                <Typography as="p" className="text-sm text-muted-foreground">
                    {subtitle}
                </Typography>
            )}

            {/* Conversion chart */}
            {chart && (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                        <thead>
                            <tr className="border-b border-border">
                                {chart.columns.map((column) => (
                                    <th key={column} className="py-2 pr-4 text-left font-medium text-foreground">
                                        {column}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {chart.rows.map((row) => (
                                <tr key={row.size} className="border-b border-border/50">
                                    <td className="py-2 pr-4 font-medium text-foreground">{row.size}</td>
                                    <td className="py-2 pr-4 text-muted-foreground">{row.us}</td>
                                    <td className="py-2 pr-4 text-muted-foreground">{row.eu}</td>
                                    <td className="py-2 pr-4 text-muted-foreground">{row.uk}</td>
                                    <td className="py-2 pr-4 text-muted-foreground">{row.cm}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* How to measure */}
            {howToMeasure && howToMeasure.length > 0 && (
                <div className="space-y-3">
                    {howToMeasure.map((step) => (
                        <div key={step.step} className="flex gap-3">
                            <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-foreground">
                                {step.step}
                            </div>
                            <div>
                                <Typography as="p" className="font-medium text-foreground">
                                    {step.heading}
                                </Typography>
                                <Typography as="p" className="text-sm text-muted-foreground">
                                    {step.body}
                                </Typography>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Size tips */}
            {sizeTips && sizeTips.tips.length > 0 && (
                <div className="rounded-none bg-muted p-4 space-y-2">
                    <Typography as="p" className="font-medium text-foreground">
                        {sizeTips.title}
                    </Typography>
                    <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                        {sizeTips.tips.map((tip) => (
                            <li key={tip}>
                                <Typography as="span" className="text-sm">
                                    {tip}
                                </Typography>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </>
    );
}
